import React, { useCallback, useEffect, useReducer } from "react";

import { injectIntl } from "gatsby-plugin-react-intl";

import EventFilters from "../components/event-filters.js";
import EventCardsMedium from "../components/event-cards-medium";
import EventSearchResults from "./event-search-results.js";
import LinkButton from "../components/link-button";
import { copy } from "./copy-handling";
import { useMicroCopy } from "../hooks/microcopy";
import { eventsReducer } from "../reducers/eventsReducer";
import {
  filterEventsByCategories,
  getCategoryFilters,
  loadEventsForDates,
  loadOngoingEvents,
  removeOngoingEvents,
  removePastEvents,
  removeSameDayDuplicates,
} from "../helpers/events";
import { loadNextPage } from "../helpers/general";

const EventCardsCombined = ({
  heading,
  intl,
  eventCategoryIds,
  showEventFilters,
  linkButton,
  siteSettings,
}) => {
  const copies = useMicroCopy();
  const locale = intl?.locale;
  const pageSize = 10;
  const ongoingEventsTimeLimitMinutes = siteSettings?.edges[0]?.node?.ongoingEventsTimeLimit ?? 30;

  const initialState = {
    selectedDates: [],
    nextPageId: 1,
    isLastPage: false,
    isLoading: false,
    eventsInRange: [],
    ongoingEvents: [],
    visibleEventsCount: pageSize,
    selectedMainCategory: null,
    selectedSubCategories: [],
  };

  const [state, dispatch] = useReducer(eventsReducer, initialState);

  const {
    selectedDates,
    nextPageId,
    isLastPage,
    isLoading,
    eventsInRange,
    ongoingEvents,
    visibleEventsCount,
    selectedMainCategory,
    selectedSubCategories,
  } = state;

  const isPreCategorized = eventCategoryIds?.length > 0;

  const collectionId = isPreCategorized
    ? `events-categories/${eventCategoryIds[0]}`
    : "events-front";

  const categoryFilters = isPreCategorized
    ? eventCategoryIds
    : getCategoryFilters(selectedMainCategory, selectedSubCategories);

  const noDatesSelected = selectedDates.length === 0;
  const noCategoriesSelected = categoryFilters.length === 0;

  const getFilteredEvents = () => {
    if (noDatesSelected && noCategoriesSelected) {
      return removeSameDayDuplicates(removePastEvents(removeOngoingEvents(eventsInRange, ongoingEvents)));
    }

    const filteredEvents = filterEventsByCategories(eventsInRange, categoryFilters);

    return removeSameDayDuplicates(noDatesSelected 
        ? removeOngoingEvents(filteredEvents, ongoingEvents) 
        : filteredEvents
    );
  };

  const filteredOngoingEvents = filterEventsByCategories(ongoingEvents, categoryFilters);

  useEffect(() => {
    if (isPreCategorized) return;

    const getOngoingEvents = async () => {
      const ongoingEvents = await loadOngoingEvents(locale, ongoingEventsTimeLimitMinutes);

      dispatch({
        type: "LOAD_ONGOING_EVENTS_SUCCESS",
        payload: {
          events: ongoingEvents,
        },
      });
    };
    getOngoingEvents();
  }, [locale, isPreCategorized, ongoingEventsTimeLimitMinutes]);

  useEffect(() => {
    dispatch({ type: "SET_LOADING", payload: true });

    const loadEvents = async () => {
      const result = noDatesSelected
        ? await loadNextPage(1, collectionId, locale)
        : await loadEventsForDates(selectedDates, locale);

      dispatch({
        type: "LOAD_EVENTS_SUCCESS",
        payload: {
          events: (result && result.items) ? result.items : [],
          visibleEventsCount: pageSize,
          nextPageId: result.nextPageId,
          isLastPage: result.isLastPage,
        },
      });
    };

    loadEvents();
  }, [selectedDates, collectionId, locale, noDatesSelected]);

  const showOngoingEvents =
    filteredOngoingEvents.length > 0 && noDatesSelected && !isPreCategorized;

  const showAllEvents = () => { 
    if (filteredOngoingEvents.length > 0 && getFilteredEvents().length === 0 && noDatesSelected) {
      return false;
    }

    return true;
  };

  const showLoadMore = () => {
    if (!showEventFilters && !isPreCategorized) {
      return false;
    } else if (noDatesSelected) {
      return getFilteredEvents().length > visibleEventsCount || !isLastPage;
    } else {
      return getFilteredEvents().length > visibleEventsCount;
    }
  };

  const handleLoadMore = async () => {
    if (getFilteredEvents().length > visibleEventsCount) {
      dispatch({
        type: "SET_VISIBLE_EVENTS_COUNT",
        payload: Math.min(visibleEventsCount + pageSize, getFilteredEvents().length),
      });
      return;
    }

    if (selectedDates.length === 0) {
      if (isLoading) return;

      const result = await loadNextPage(nextPageId, collectionId, locale);
      const combinedEvents = [...eventsInRange, ...result.items];

      dispatch({
        type: "LOAD_EVENTS_SUCCESS",
        payload: {
          events: combinedEvents,
          nextPageId: result.nextPageId,
          visibleEventsCount: visibleEventsCount + pageSize,
          isLastPage: result.isLastPage,
        },
      });
    }
  };

  const onCategoryChange = useCallback(
    (categories) => dispatch({ type: "SET_CATEGORY_FILTERS", payload: categories }),
    [],
  );

  return (
    <div
      className={`c-rich-text xl:max-w-layout-xl xxl:max-w-layout-xxl mx-auto mb-20 md:mb-36 lg:mb-40 ${
        showEventFilters ? "min-h-screen" : ""
      }`}
    >
      {showEventFilters && (
        <EventFilters
          selectedDates={selectedDates}
          onDateChange={(dates) =>
            dispatch({
              type: "SET_SELECTED_DATES",
              payload: { dates: dates, visibleEventsCount: pageSize },
            })
          }
          selectedMainCategory={selectedMainCategory}
          selectedSubCategories={selectedSubCategories}
          onMainCategoryChange={(category) => {
            dispatch({
              type: "SET_SELECTED_MAIN_CATEGORY",
              payload: category,
            });
          }}
          onSubCategoryChange={(categories) => {
            dispatch({
              type: "SET_SELECTED_SUB_CATEGORIES",
              payload: categories,
            });
          }}
          onCategoryChange={onCategoryChange}
          onClearDates={() => {
            dispatch({
              type: "CLEAR_DATES",
              payload: {
                visibleEventsCount: pageSize,
                nextPageId: 1,
              },
            });
          }}
          showRightNow={filteredOngoingEvents.length > 0}
        />
      )}

      {showOngoingEvents && (
        <section className="my-20 md:my-36 lg:my-40">
          <h1>{copy(copies, "button.rightNow", locale)}</h1>
          <EventCardsMedium eventPosts={filteredOngoingEvents} intl={intl} showCategories={true} />
        </section>
      )}

      {showAllEvents() && (
        <section className="my-20 md:my-36 lg:my-40">
          {(!showEventFilters || showOngoingEvents) && <h1>{heading}</h1>}
          <EventSearchResults
            events={getFilteredEvents().slice(0, visibleEventsCount)}
            isLoading={isLoading}
            showLoadMore={showLoadMore}
            handleLoadMore={handleLoadMore}
            showClearFilters={!isPreCategorized}
            handleClearFilters={() => dispatch({ type: "CLEAR_SELECTED_CATEGORIES" })}
            locale={locale}
          />
          {linkButton && !isLoading && (
            <LinkButton
              linkName={linkButton?.name}
              target={linkButton?.page?.slug}
              isExternal={false}
              className="mt-4 md:mt-8 lg:mt-12"
            ></LinkButton>
          )}
        </section>
      )}
    </div>
  );
};

export default injectIntl(EventCardsCombined);
